import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import moment from 'moment';
import {NbDialogService, NbPopoverDirective} from '@nebular/theme';
import {TranslateService} from '@ngx-translate/core';
import {TimeTrackingService, TrackedStatus} from '../../shared/time-tracking.service';
import {TripEnd, TripRow, TripWeek} from '../../interfaces/trip';
import {ExportDialogComponent} from '../export-dialog/export-dialog.component';
import {AuthService} from '../../shared/auth.service';
import {NotifierService} from '../../shared/notifier.service';
import {RegistrationWeekService} from '../../registration/registration-week.service';
import {endReady} from './trip-end/trip-end.component';
import {DraftErrors, draftPreview, formatKm, groupKey, parseKm, sameRoute, serverErrors, toRow, TripDraft, validate} from './trip-draft';

// The first invalid field of the open row: a draft km cell, then the editor's fields (an address wrapper is focusable, tabindex -1).
const INVALID = '.draft-row [aria-invalid="true"], .row-editor [aria-invalid="true"], .row-editor .has-error';
const FOCUSABLE = 'input:not([disabled]), select, button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const blankEnd = (): TripEnd => ({home: false, street: '', number: '', postcode: '', town: ''});

// One Hours-style card on the desktop grid; start is the first row's index in the flat card
// order, which data-cell and cellKey use so Enter/arrows run down the cards as shown.
export interface TripGroup {
  key: string;
  kind: 'commute' | 'project' | 'other';
  name: string;
  // The project's colour dot and "for <client>", as Hours shows them.
  color?: string;
  client?: string;
  Project_id: number | null;
  rows: TripRow[];
  start: number;
  days: number[];
  total: number;
}

// Commute first, then one card per project in first-seen order, then business trips without one.
export function groupTrips(rows: TripRow[]): TripGroup[] {
  const byKey = new Map<string, TripGroup>();
  for (const row of rows) {
    const key = groupKey(row);
    const kind = key === 'commute' ? 'commute' : key === 'other' ? 'other' : 'project';
    if (!byKey.has(key)) {
      byKey.set(key, {key, kind, name: row.project ?? '', color: row.project_color, client: row.project_client,
                      Project_id: kind === 'project' ? row.Project_id : null,
                      rows: [], start: 0, days: Array(7).fill(0), total: 0});
    }
    const group = byKey.get(key);
    group.rows.push(row);
    row.km.forEach((km, d) => { group.days[d] += +km || 0; group.total += +km || 0; });
  }
  const rank = {commute: 0, project: 1, other: 2};
  const groups = [...byKey.values()].sort((a, b) => rank[a.kind] - rank[b.kind]);
  let start = 0;
  for (const group of groups) { group.start = start; start += group.rows.length; }
  return groups;
}

// Non-colour cue for a tinted cell.
const STATE_TITLES = {'st-sub': 'registration.status.submitted', 'st-appr': 'registration.status.approved',
                      'st-rej': 'registration.status.rejected',
                      'st-inv': 'tracking.trips.invoiced'};

/**
 * Trips on the weekly timesheet (#874): one row per route, purpose and project with
 * km per weekday. Rows lock with the week; another person's trips are read-only.
 */
@Component({
  selector: 'bst-week-trips',
  templateUrl: './week-trips.component.html',
  styleUrls: ['./week-trips.component.scss'],
  standalone: false
})
export class WeekTripsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() weekStart: string;
  @Input() userId: number = null;
  @Input() locked = false;
  // Rows in the week, so a week with only trips can be submitted.
  @Output() tripCount = new EventEmitter<number>();
  // Week paging is the Km page's (KilometresComponent.page); the desktop header card only draws its arrows.
  @Input() paging = false;
  @Output() paged = new EventEmitter<number>();
  // True while a row's editor is open, so the Km page locks its person picker (switching people would drop it).
  @Output() editing = new EventEmitter<boolean>();

  private _week: TripWeek = null;
  days: moment.Moment[] = [];
  today = moment();
  private _form: TripDraft = null;
  // A new trip's card: 'commute' or 'p<id>' from a card's Add line, 'new' for the temporary New trip card.
  newIn: string = null;
  busy = false;
  loadFailed = false;
  // A refused cell, shown under the grid until the next commit.
  cellError: {row: string, day: number} = null;
  // Read out by the polite live region after an add, save or remove.
  announcement = '';
  // Single-day phone cards below 600; from 600 the week grid, tightened below 768 (user, 2026-10-07).
  narrow = false;
  selectedDay = 0;
  // The phone day-nav stepped past Monday or Sunday: the day to land on once that week is in.
  private pendingDay: {week: string, day: number} = null;
  // The row whose kebab menu was opened last, so Esc can hand focus back to it.
  private menuRow: string = null;
  // Collapsed cards (Hours' open-button).
  closed = new Set<string>();
  @ViewChildren(NbPopoverDirective) popovers: QueryList<NbPopoverDirective>;
  readonly stateTitles: Record<string, string> = STATE_TITLES;
  private grouped: {rows: TripRow[], projects: TripWeek['projects'], groups: TripGroup[]} = null;
  // Local generation counter (Codex 2): a week/user change bumps it so a slower, now
  // superseded getTrips() response is dropped instead of overwriting the new context's count.
  private gen = 0;
  // Every getTrips (load and reload) and every write response takes the next number; one lands only if it is newer than
  // the last that landed, so an older pending load cannot overwrite a newer reload. Separate
  // from gen, which a reload must not bump (queued saves would read as superseded).
  private readSeq = 0;
  private readApplied = 0;
  // Writes run one after another, so an older response never lands on top of a newer one.
  private queue: Promise<unknown> = Promise.resolve();
  // The newest km queued per cell (row-date), so a second quick edit compares against it, not
  // the week from before the first one landed. Box identity tells a superseded edit apart.
  private queued = new Map<string, {km: number | null}>();
  // The draft whose save is queued or in flight. Another Done on it is ignored until it settles (the
  // button shows a spinner): refused, the editor stays open for the next Done; saved, it closes. Per
  // draft, so a save left over from another week never blocks a newly opened editor.
  private saving: TripDraft | null = null;

  get savingForm(): boolean {
    return !!this.form && this.saving === this.form;
  }
  // Refused-save messages from the server; client errors are recomputed live once a save was tried.
  private refused: DraftErrors = {};
  private tried = false;
  // The form as it opened, so Cancel only asks when something was changed.
  private opened = '';

  constructor(private time: TimeTrackingService, private dialogs: NbDialogService, public auth: AuthService,
              private weekSvc: RegistrationWeekService, private host: ElementRef<HTMLElement>,
              private translate: TranslateService, private notifier: NotifierService) {}

  ngOnInit() {
    this.layout();
  }

  @HostListener('window:resize')
  layout() {
    this.narrow = window.innerWidth < 600;
  }

  get form(): TripDraft {
    return this._form;
  }

  set form(draft: TripDraft) {
    if (!!draft !== !!this._form) { this.editing.emit(!!draft); }
    this._form = draft;
  }

  get week(): TripWeek {
    return this._week;
  }

  set week(week: TripWeek) {
    this._week = week;
    this.tripCount.emit(week?.rows?.length ?? 0);
    // A cell cleared the open row's last km, so the server dropped the row (TripController::cell): its editor goes too.
    if (week && this.form?.row_id && !week.rows.some(row => row.row_id === this.form.row_id)) {
      this.form = null;
      this.announcement = 'tracking.trips.removed';
    // A reload or write came back Submitted/Approved (another tab, an approver): the week is read-only now,
    // and ngOnChanges never sees it because no input changed. Must run after the backing field is set,
    // since `editable` reads this.week.
    } else if (week && this.form && !this.editable) {
      this.form = null;
      this.newIn = null;
    // A card's draft is drawn in that card: when the card lost its last row, the New trip card takes it.
    } else if (week && this.form && !this.form.row_id && this.newIn !== 'new' && !this.groups.some(g => g.key === this.newIn)) {
      this.newIn = 'new';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const contextChanged = !!(changes['weekStart'] || changes['userId']);
    if (contextChanged || (changes['locked'] && this.locked)) {
      this.form = null;
      this.newIn = null;
      this.tried = false;
      this.refused = {};
    }
    this.cellError = null;
    this.days = Array.from({length: 7}, (_, i) => moment(this.weekStart).add(i, 'days'));
    if (contextChanged) {
      const pending = this.pendingDay && !changes['userId'] && moment(this.weekStart).isSame(this.pendingDay.week, 'day');
      this.selectedDay = pending ? this.pendingDay.day : moment().isoWeekday() - 1;
      this.pendingDay = null;
    }
    return this.load(contextChanged);
  }

  retry() {
    return this.load(true);
  }

  private async load(contextChanged: boolean) {
    if (contextChanged) {
      // New week or user: the old row count no longer describes what's on screen.
      // Clear it now so Submit can't stay enabled on an empty new week while the
      // new count is still loading (Codex 2); a same-context reload (locked-only
      // change, or no change) keeps the count, M3 stays fixed.
      this.week = null;
    }
    const token = ++this.gen;
    const seq = ++this.readSeq;
    this.weekSvc.beginLoad();
    let week: TripWeek = null;
    try {
      week = await this.time.getTrips(this.weekStart, this.userId).catch(() => null);
    } finally {
      this.weekSvc.endLoad();
    }
    if (token !== this.gen || seq < this.readApplied) { return; } // superseded by a newer week/user change or read
    // A same-context reload failure (recall/reopen hiccup, transient network blip) must
    // not replace a known count with an unknown one (Codex new 2): keep the retained week.
    // On a context change the count genuinely isn't known yet; it's already cleared above.
    if (week === null && !contextChanged) { return; }
    this.loadFailed = week === null;
    this.readApplied = seq;
    this.week = week;
  }

  // Inputs only on your own week while it is open, by the parent's lock or the trips' own
  // status. busy does not take them away: disabling a focused input drops focus, and writes
  // are queued instead (run()).
  get editable(): boolean {
    const status = this.week?.status;
    return !this.locked && !this.userId && status !== TrackedStatus.Submitted && status !== TrackedStatus.Approved;
  }

  get canAdd(): boolean {
    return this.editable && this.week?.vehicle !== 'none';
  }

  // Approval is per week, so every cell takes the week's colour. A rejected week is editable
  // again but stays red until resubmitted. Invoicing stays the row badge: there is no per-day API.
  get cellState(): string {
    switch (this.week?.status) {
      case TrackedStatus.Submitted: return 'st-sub';
      case TrackedStatus.Approved: return 'st-appr';
      case TrackedStatus.Rejected: return 'st-rej';
      default: return 'st-draft';
    }
  }

  // A fully invoiced row turns navy; a partly invoiced one keeps the week colour and its chip
  // (there is no per-day invoiced API).
  rowState(row: TripRow): string {
    return row.invoiced && row.invoiced === row.km.filter(km => +km > 0).length ? 'st-inv' : this.cellState;
  }

  // Like Hours, the cards you can fill show before they have a trip: Commute, then each assigned project.
  get groups(): TripGroup[] {
    const rows = this.week?.rows ?? [];
    const projects = this.canAdd ? this.week.projects : null;
    if (this.grouped?.rows !== rows || this.grouped.projects !== projects) {
      const groups = groupTrips(rows);
      const blank = (kind: 'commute' | 'project', key: string, p?: TripWeek['projects'][number]): TripGroup =>
        ({key, kind, name: p?.name ?? '', color: p?.color, client: p?.client, Project_id: p?.id ?? null, rows: [], start: rows.length, days: Array(7).fill(0), total: 0});
      if (projects && !groups.some(g => g.key === 'commute')) { groups.unshift(blank('commute', 'commute')); }
      const missing = (projects ?? []).filter(p => !groups.some(g => g.key === `p${p.id}`));
      groups.splice(groups.filter(g => g.kind !== 'other').length, 0, ...missing.map(p => blank('project', `p${p.id}`, p)));
      this.grouped = {rows, projects, groups};
    }
    return this.grouped.groups;
  }

  toggle(key: string) {
    if (!this.closed.delete(key)) { this.closed.add(key); }
  }

  // A card's Add line: only where Add is allowed at all (canAdd: editable, has a vehicle), and then Commute
  // or a project the person is on this week (storeRow refuses others); never Other.
  canAddTo(g: TripGroup): boolean {
    return this.canAdd
      && (g.kind === 'commute' || (g.kind === 'project' && !!this.week?.projects.some(p => p.id === g.Project_id)));
  }

  // One select for travel type and project, so a saved row never lands in the wrong card.
  get choice(): 'commute' | number | null {
    return this.form.purpose === 'commute' ? 'commute' : this.form.Project_id;
  }

  choose(value: 'commute' | number) {
    this.form.purpose = value === 'commute' ? 'commute' : 'business';
    this.form.Project_id = value === 'commute' ? null : value;
  }

  // This week's projects, plus the open row's own when it is no longer on them.
  get projectOptions(): {id: number, name: string}[] {
    const projects = this.week?.projects ?? [];
    const id = this.form?.Project_id;
    return id == null || projects.some(p => p.id === id) ? projects : [...projects, {id, name: this.form.project ?? ''}];
  }

  // Save stays clickable; a tried save names what is missing. endReady also checks the server's lengths.
  get errors(): DraftErrors {
    if (!this.tried || !this.form) { return this.refused; }
    const errors = {...this.refused, ...validate(this.form)};
    for (const end of ['from', 'to'] as const) {
      if (!errors[end] && !endReady(this.form[end])) { errors[end] = 'tracking.trips.err_address'; }
    }
    return errors;
  }

  get preview() {
    return draftPreview(this.form);
  }

  get draftTotal(): number {
    return this.form.km.reduce((sum, km) => sum + (parseKm(km) || 0), 0);
  }

  get weekNumber(): number {
    return moment(this.weekStart).isoWeek();
  }

  // Same wording as the Hours header (timesheet.component.ts inweek).
  get weekOffset(): number {
    return Math.ceil(moment(this.weekStart).diff(moment().startOf('day'), 'weeks', true));
  }

  get weekLabel(): string {
    const n = this.weekOffset;
    return n === -1 ? 'tracking.prevweek' : n === 1 ? 'tracking.nextweek' : n > 0 ? 'tracking.inweek' : n < 0 ? 'tracking.outweek' : 'tracking.thisweek';
  }

  // Each end is Home or a full address; a half-picked Places result never reaches the server.
  get endsReady(): boolean {
    return !!this.form && endReady(this.form.from) && endReady(this.form.to);
  }

  // The person's trip overview (#1514); an Admin viewing someone gets theirs.
  openOverview() {
    const context = {title: 'tracking.trips.overview', formats: ['pdf', 'csv'], maxYear: true};
    this.dialogs.open(ExportDialogComponent, {context}).onClose.subscribe(result => {
      if (result) {
        this.time.downloadTripOverview(result.start, result.end, this.userId, result.format).catch(() => null);
      }
    });
  }

  // A week on an invoice is approved and stays so, so "Invoiced" replaces that status; a row split over two invoices counts its days.
  invoicedLabel(row: TripRow): string {
    if (row.invoiced === row.km.filter(km => +km > 0).length) { return 'tracking.trips.invoiced'; }
    return row.invoiced === 1 ? 'tracking.trips.invoiced_days_one' : 'tracking.trips.invoiced_days';
  }

  address(end: TripEnd): string {
    return `${end.street} ${end.number}, ${end.postcode} ${end.town}`;
  }

  fmt(km: number | null): string {
    return formatKm(km, this.translate?.currentLang ?? 'en');
  }

  total(row: TripRow): number {
    return row.km.reduce((sum, km) => sum + (+km || 0), 0);
  }

  dayTotal(day: number): number {
    return this.week.rows.reduce((sum, row) => sum + (+row.km[day] || 0), 0);
  }

  get weekTotal(): number {
    return this.week.rows.reduce((sum, row) => sum + this.total(row), 0);
  }

  // A card's Add line drafts the trip in that card, preset to it; the bottom Add and a day head draft it in a New trip card.
  add(group?: TripGroup, day?: number) {
    this.open({Project_id: group?.Project_id ?? null, purpose: group?.kind === 'commute' ? 'commute' : 'business', description: '',
               from: blankEnd(), to: blankEnd(), km: Array(7).fill(''), return: false},
              group?.key ?? 'new', day == null ? '[data-focus="from"]' : `[data-cell="draft-${day}"]`);
  }

  // Phone day-nav: past Monday or Sunday it pages the week, like Hours' stepDay().
  stepDay(dir: number) {
    const day = this.selectedDay + dir;
    if (day >= 0 && day <= 6) { this.selectedDay = day; return; }
    this.pendingDay = {week: moment(this.weekStart).add(dir, 'weeks').format('YYYY-MM-DD'), day: dir < 0 ? 6 : 0};
    this.paged.emit(dir);
  }

  // Last week's routes and km into this week (TripController::copyWeek); a route already there is left alone.
  async copyLastWeek() {
    // Counted when the queued write starts, after the cell saves ahead of it have landed.
    const weekStart = this.weekStart;
    let before = 0;
    if (!await this.run(() => { before = this.tripDays; return this.time.copyTripWeek(weekStart); }) || weekStart !== this.weekStart) { return; }
    const added = this.tripDays - before;
    this.notifier.notify(added ? 'tracking.trips.copied' : 'tracking.trips.nothingtocopy', null, 'info', null, 3500, {value: added});
  }

  private get tripDays(): number {
    return (this.week?.rows ?? []).reduce((sum, row) => sum + row.km.filter(km => +km > 0).length, 0);
  }

  edit(row: TripRow) {
    this.open({...row, from: {...row.from}, to: {...row.to}, km: [], return: false}, null);
  }

  // Cancel and a bare Esc: a changed form asks first. A week change still drops it through cancel().
  async tryCancel() {
    const draft = this.form;
    if (!await this.canLeave()) { return; }
    if (this.form === draft) { this.cancel(); }
  }

  private get changed() { return !!this.form && JSON.stringify(this.form) !== this.opened; }

  // Cancel's question, also the Km route's canDeactivate (KilometresComponent.canLeave). A double
  // click on a nav link asks twice while the first dialog is up; both get its one answer.
  async canLeave(): Promise<boolean> {
    return !this.changed || (this.asking ??= this.notifier.confirm('tracking.trips.discard_check', this.translate?.instant('tracking.trips.discard_body'), 'tracking.trips.discard', 'danger', 'cancel')
      .finally(() => this.asking = undefined));
  }
  private asking?: Promise<boolean>;

  cancel() {
    const {form, newIn} = this;
    this.form = null;
    this.newIn = null;
    if (!form) { return; }
    this.focus(form.row_id ? `[data-route="${form.row_id}"]` : newIn === 'new' ? '[data-focus="add"]' : `[data-group="${newIn}"] [data-action="add-line"]`);
  }

  async save() {
    // Before the resets below: an ignored Done must not wipe the first save's server errors.
    if (this.savingForm) { return; }
    this.tried = true;
    this.refused = {};
    if (Object.keys(this.errors).length) { return this.focus(INVALID); }
    const draft = this.form;
    const {km, return: back, ...row} = draft;
    const date = this.weekStart;
    const token = this.gen;
    // A save that lands after a week change belongs to an editor that is gone; leave the new one alone.
    const stale = () => token !== this.gen || this.form !== draft;
    // Clearing a row's last km deletes it (TripController::cell). The check runs inside the queued
    // write, after every cell write queued before Done has landed, so updateRow never goes out for a
    // row that is gone; the week setter (Task 3) has closed the editor by then. Do not await the
    // queue before run(): that lets a later cell write slip in ahead of the update.
    const gone = () => !!draft.row_id && !this.week?.rows.some(r => r.row_id === draft.row_id);
    const skip = () => stale() || gone();
    const before = new Set((this.week?.rows ?? []).map(r => r.row_id));
    this.saving = draft;
    const saved = await this.run(() => skip() ? Promise.resolve(this.week)
      : draft.row_id ? this.time.updateTripRow(row as TripRow)
      : this.time.addTripRow({...toRow(draft), date}), e => { if (!stale()) { this.refused = serverErrors(e); } })
      .finally(() => { if (this.saving === draft) { this.saving = null; } });
    if (skip()) { return; }
    if (!saved) { return this.focus(`${INVALID}, .row-editor [role="alert"]`); }
    this.form = null;
    this.newIn = null;
    // storeRow puts a route already in the week on its row (on free days), so fewer rows appear than routes added.
    const added = (this.week?.rows ?? []).filter(r => !before.has(r.row_id));
    const joined = !draft.row_id && added.length < (back ? 2 : 1);
    this.announcement = draft.row_id ? 'tracking.trips.saved' : joined ? 'tracking.trips.joined' : 'tracking.trips.added';
    if (joined) { this.notifier.notify('tracking.trips.joined', null, 'info'); }
    // Back to the saved row (it may have moved card), the outbound route just added, or the row it joined.
    const same = (r: TripRow) => sameRoute(r, draft, this.week.home);
    this.focusRow(draft.row_id ?? added.find(same)?.row_id ?? this.week?.rows.find(same)?.row_id);
  }

  private focusRow(rowId: string | undefined) {
    const row = this.week?.rows.find(r => r.row_id === rowId);
    if (!row) { return this.focus('[data-focus="add"]'); }
    this.closed.delete(groupKey(row));
    this.focus(`[data-route="${rowId}"]`);
  }

  private open(draft: TripDraft, newIn: string, focus = '[data-focus="from"]') {
    if (this.form || !this.editable) { return; }   // one open row; a kebab Edit cannot open a second
    this.tried = false;
    this.refused = {};
    this.form = draft;
    this.newIn = newIn;
    this.opened = JSON.stringify(draft);
    this.popovers?.forEach(popover => popover.hide());
    // The editor is drawn in its card, so a collapsed card opens.
    this.closed.delete(newIn ?? groupKey(draft));
    this.focus(focus);
  }

  // An open select in the editor takes the Esc; the Places field stops its own. Only a bare Esc cancels.
  escape($event: KeyboardEvent) {
    const editor = ($event.target as HTMLElement).closest('.row-editor, .draft-row')?.parentElement ?? this.host.nativeElement;
    if (editor.querySelector('nb-select.open')) { return; }
    if (!this.changed) { return this.tryCancel(); }
    // Nebular's closeOnEsc shuts a dialog on the Esc keyup: ask once this press is up, or it closes at once.
    const draft = this.form;
    const up = (k: KeyboardEvent) => {
      if (k.key !== 'Escape') { return; }
      document.removeEventListener('keyup', up);
      if (this.form === draft) { this.tryCancel(); }
    };
    document.addEventListener('keyup', up);
  }

  // On the editor and the draft row: Ctrl/Cmd+Enter is Done, Esc is Cancel. Plain Enter keeps moving down
  // the cells and picking a Places result.
  editorKey(e: KeyboardEvent) {
    if (e.key === 'Escape') { return this.escape(e); }
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) { return; }
    e.preventDefault();
    this.save();
  }

  openMenu(row: TripRow) {
    this.menuRow = row.row_id;
  }

  // Esc closes an open row menu and gives focus back to its kebab.
  @HostListener('document:keydown.escape')
  closeMenu() {
    const shown = this.popovers?.filter(popover => popover.isShown) ?? [];
    if (!shown.length) { return; }
    shown.forEach(popover => popover.hide());
    this.focus(`[data-menu="${this.menuRow}"]`);
  }

  ngOnDestroy() {
    this.form = null;
  }

  // After the next render (zone.js runs change detection before the timeout fires). No host
  // when the component is constructed directly in a spec. A wrapper (tabindex -1) hands focus
  // to its first control, and keeps it when it has none.
  private focus(selector: string) {
    setTimeout(() => {
      const el = this.host?.nativeElement.querySelector<HTMLElement>(selector);
      const inner = el?.matches('[tabindex="-1"]') ? el.querySelector<HTMLElement>(FOCUSABLE) : null;
      (inner ?? el)?.focus();
    });
  }

  private date(day: number): string {
    return moment(this.weekStart).add(day, 'days').format('YYYY-MM-DD');
  }

  private km(rowId: string, day: number): number | null {
    return this.week?.rows.find(row => row.row_id === rowId)?.km[day] ?? null;
  }

  async commitCell(row: TripRow, day: number, input: HTMLInputElement) {
    const km = parseKm(input.value);
    if (Number.isNaN(km)) {
      this.cellError = {row: row.row_id, day};
      input.value = this.fmt(this.km(row.row_id, day));
      return;
    }
    this.cellError = null;
    // Taken now: the write may wait in the queue past a week change.
    const date = this.date(day);
    const key = `${row.row_id}-${date}`;
    const last = this.queued.has(key) ? this.queued.get(key).km : this.km(row.row_id, day);
    if ((km || null) === last) {
      input.value = this.fmt(km || null);
      return;
    }
    const mine = {km: km || null};
    this.queued.set(key, mine);
    const typed = input.value;
    await this.run(() => this.time.setTripCell({row_id: row.row_id, date, km: km ?? 0}));
    if (this.queued.get(key) !== mine) { return; }   // a newer edit to this cell writes back
    this.queued.delete(key);
    if (input.ownerDocument.activeElement === input && input.value !== typed) { return; }   // still typing
    // After a refused write the reload often returns the km the cell already showed, so the
    // [value] binding does not change and the typed text would stay: write the server value back.
    input.value = this.fmt(this.km(row.row_id, day));
  }

  // Hours' spreadsheet keys (timesheet.component.ts 730-741): Enter / Down to the same day one
  // row down, Up one row up; Left/Right stay in the text. Blur commits through (change).
  // Steps through the day's cells in DOM order, so a collapsed card (rows not rendered) keeps the
  // order shown.
  cellKey(e: KeyboardEvent, day: number, row?: TripRow) {
    const input = e.target as HTMLInputElement;
    // Esc drops what was typed, so the blur that follows has nothing to write: back to the km commitCell
    // compares against, the queued one while a write to this cell is pending. Draft cells have no row.
    if (e.key === 'Escape' && row) {
      const key = `${row.row_id}-${this.date(day)}`;
      input.value = this.fmt(this.queued.has(key) ? this.queued.get(key).km : this.km(row.row_id, day));
    }
    // Ctrl/Cmd+Enter and Esc are the open row's Done and Cancel (editorKey). Its own cells sit outside
    // the editor, so they hand the key over; blurring first commits the typed km ahead of Done.
    if (e.ctrlKey || e.metaKey || e.key === 'Escape') {
      if (this.form?.row_id && input.closest(`[data-row="${this.form.row_id}"]`)) {
        if (e.key === 'Enter') { input.blur(); }
        this.editorKey(e);
      }
      return;
    }
    const step = e.key === 'Enter' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (!step) { return; }
    e.preventDefault();
    // A draft's cells stay put: the next row down is a saved row, whose cells write at once.
    if (input.closest('.draft-row')) { return; }
    const root: ParentNode = this.host.nativeElement;
    const cells = Array.from(root.querySelectorAll<HTMLInputElement>(`input[data-cell$="-${day}"]`));
    const next = cells[cells.indexOf(input) + step];
    if (next) { next.focus(); next.select(); }
    else if (e.key === 'Enter') { input.blur(); }
  }

  // Removes the route on every day of the week, so it asks first like Hours' removeRow().
  async remove(row: TripRow) {
    this.popovers?.forEach(popover => popover.hide());
    const home = this.translate?.instant('tracking.trips.home') ?? 'Home';
    const route = `${row.from.home ? home : this.address(row.from)} → ${row.to.home ? home : this.address(row.to)}`;
    const {weekStart, userId} = this;
    const ok = await this.notifier.confirm('tracking.trips.remove_check', route, 'tracking.trips.remove', 'danger', 'cancel');
    // The week or person changed while the dialog was open: that row is no longer on screen.
    if (weekStart !== this.weekStart || userId !== this.userId) { return; }
    if (!ok) { return this.focus(`[data-menu="${row.row_id}"]`); }
    if (await this.run(() => this.time.removeTripRow(row.row_id))) { this.announcement = 'tracking.trips.removed'; }
  }

  // A refused write (locked week, validation) is toasted by the service; reload so the grid
  // matches the server. A write that lands after a week/user change belongs to the old week.
  private run(write: () => Promise<TripWeek>, refused?: (e: any) => void): Promise<boolean> {
    // Taken when the write is queued, so a write still waiting behind another when the week
    // changes does not land its response on the new week either.
    const token = this.gen;
    const {weekStart, userId} = this;
    const done = this.queue.then(async () => {
      this.busy = true;
      try {
        const week = await write();
        // Superseded, but the same week may be on screen again (A -> B -> A) with an older load.
        if (token !== this.gen) { await this.reload(weekStart, userId); }
        else {
          // Numbered on arrival, so every read started before the response is older than it.
          this.readApplied = ++this.readSeq;
          this.week = week;
        }
        return true;
      } catch (e) {
        refused?.(e);
        await this.reload(weekStart, userId);
        return false;
      } finally {
        this.busy = false;
      }
    });
    this.queue = done.catch(() => null);
    this.weekSvc.trackWrite(done);
    return done;
  }

  // Only while that week and user are on screen; a load or change meanwhile wins (gen).
  private async reload(weekStart: string, userId: number) {
    if (weekStart !== this.weekStart || userId !== this.userId) { return; }
    const token = this.gen;
    const seq = ++this.readSeq;
    const week = await this.time.getTrips(weekStart, userId).catch(() => null);
    if (week && token === this.gen && seq > this.readApplied) {
      this.readApplied = seq;
      this.week = week;
    }
  }
}
